import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import styles from "./OrderHistory.module.css";
import KeywordFilter from "../KeywordFilter";
import useMyAccount from "../../../../hooks/useMyAccount";
import { useAuth } from "../../../../hooks/useAuth";
import { API_URLS, DEFAULT_HEADERS } from "../../../../util/constants";
import { handleApiError } from "../../../../util/errorHandler";

export const OrderHistory = () => {
  const [keyword, setKeyword] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [buyerFilter, setBuyerFilter] = useState("");
  const [companyFilter, setCompanyFilter] = useState("");
  const [isDownloadingReportPdf, setIsDownloadingReportPdf] = useState(false);

  const handleFilterChange = (event) => {
    setKeyword(event.target.value);
  };

  const { userData, orderHistory, setOrderDetailId, refreshMyAccount, isRefreshingMyAccount } =
    useMyAccount();
  const { token } = useAuth();

  const isMasterUser = Boolean(userData?.is_master_user);
  const isShoppingCenter = Boolean(userData?.is_shopping_center);

  const buyerOptions = useMemo(() => {
    const set = new Set();
    (orderHistory || []).forEach((o) => {
      const v = o?.buyer_name;
      if (typeof v === "string" && v.trim()) set.add(v.trim());
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [orderHistory]);

  const companyOptions = useMemo(() => {
    const set = new Set();
    (orderHistory || []).forEach((o) => {
      const v = o?.company_name;
      if (typeof v === "string" && v.trim()) set.add(v.trim());
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [orderHistory]);

  const loadOrderDetailHandler = (id) => {
    setOrderDetailId(id);
  };

  const parseOrderDate = (raw) => {
    if (!raw || typeof raw !== "string") return null;
    // Formato esperado no histórico: "DD/MM/YYYY HH:mm"
    // Ex.: "20/04/2026 11:46"
    const s = raw.trim();
    const m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}):(\d{2}))?$/);
    if (!m) return null;
    const dd = parseInt(m[1], 10);
    const mm = parseInt(m[2], 10);
    const yyyy = parseInt(m[3], 10);
    const hh = m[4] ? parseInt(m[4], 10) : 0;
    const min = m[5] ? parseInt(m[5], 10) : 0;
    if (!dd || !mm || !yyyy) return null;
    const d = new Date(yyyy, mm - 1, dd, hh || 0, min || 0, 0, 0);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const filteredOrders = useMemo(() => {
    const kw = (keyword || "").trim().toLowerCase();
    const buyerKw = (buyerFilter || "").trim().toLowerCase();
    const companyKw = (companyFilter || "").trim().toLowerCase();

    const fromDate = dateFrom ? new Date(`${dateFrom}T00:00:00`) : null;
    const toDate = dateTo ? new Date(`${dateTo}T23:59:59`) : null;
    const fromOk = fromDate && !Number.isNaN(fromDate.getTime()) ? fromDate : null;
    const toOk = toDate && !Number.isNaN(toDate.getTime()) ? toDate : null;

    return (orderHistory || []).filter((o) => {
      if (!o || typeof o !== "object") return false;

      // Keyword filter (genérico)
      if (kw) {
        const hay = [
          o.id_order,
          o.buyer_name,
          o.company_name,
          o.status_order_name,
          o.order_date,
          o.total_price,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!hay.includes(kw)) return false;
      }

      // Buyer filter (somente master)
      if (isMasterUser && buyerKw) {
        const b = (o.buyer_name || "").toLowerCase();
        if (!b.includes(buyerKw)) return false;
      }

      // Company filter (somente central de compras)
      if (isShoppingCenter && companyKw) {
        const c = (o.company_name || "").toLowerCase();
        if (!c.includes(companyKw)) return false;
      }

      // Date range filter
      if (fromOk || toOk) {
        const od = parseOrderDate(o.order_date);
        if (!od) return false;
        if (fromOk && od < fromOk) return false;
        if (toOk && od > toOk) return false;
      }

      return true;
    });
  }, [
    buyerFilter,
    companyFilter,
    dateFrom,
    dateTo,
    isMasterUser,
    isShoppingCenter,
    keyword,
    orderHistory,
  ]);

  const downloadOrdersReportPdfHandler = async () => {
    if (!token) return;
    setIsDownloadingReportPdf(true);
    try {
      const payload = {
        token,
        date_from: dateFrom || null,
        date_to: dateTo || null,
      };

      const response = await fetch(API_URLS.ORDERS_REPORT_PDF, {
        method: "POST",
        headers: DEFAULT_HEADERS.JSON_WITH_CORS,
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        let payloadErr = null;
        try {
          payloadErr = await response.json();
        } catch {
          // ignore
        }
        const err = new Error(payloadErr?.message || payloadErr?.error || "Falha ao gerar PDF do relatório");
        err.status = response.status;
        err.response = payloadErr;
        throw err;
      }

      const contentType = response.headers.get("content-type") || "";
      if (!contentType.includes("application/pdf")) {
        let payloadErr = null;
        try {
          payloadErr = await response.json();
        } catch {
          // ignore
        }
        const err = new Error(
          payloadErr?.message ||
          payloadErr?.error ||
          "Resposta inesperada ao gerar PDF do relatório"
        );
        err.status = response.status;
        err.response = payloadErr;
        throw err;
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (error) {
      handleApiError(error, { context: "OrderHistory.downloadOrdersReportPdfHandler", showToast: false });
    } finally {
      setIsDownloadingReportPdf(false);
    }
  };

  return (
    <>
      <div className={`d-flex ${isMasterUser ? "justify-content-between" : "justify-content-end"} mb-3`} style={{ gap: "10px" }}>

        {
          isMasterUser && (
            <p className="">Aqui você verá os pedidos de todos compradores desta unidade</p>
          )
        }

        <button
          type="button"
          className="theme-btn-1 btn btn-effect-1"
          onClick={refreshMyAccount}
          disabled={isRefreshingMyAccount}
          title={isRefreshingMyAccount ? "Atualizando..." : "Atualizar dados"}
          style={{ padding: "10px 16px" }}
        >
          {isRefreshingMyAccount ? "Atualizando..." : "Atualizar dados"}
        </button>
      </div>
      <KeywordFilter keyword={keyword} onFilterChange={handleFilterChange} />

      <div className="row mt-3" style={{ rowGap: "12px" }}>
        <div className="col-12 col-md-3">
          <label className="form-label" style={{ fontSize: "14px" }}>
            Data inicial
          </label>
          <input
            type="date"
            className="form-control"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
          />
        </div>
        <div className="col-12 col-md-3">
          <label className="form-label" style={{ fontSize: "14px" }}>
            Data final
          </label>
          <input
            type="date"
            className="form-control"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
          />
        </div>

        {isMasterUser && (
          <div className="col-12 col-md-3">
            <label className="form-label" style={{ fontSize: "14px" }}>
              Comprador
            </label>
            <select
              className="form-control"
              value={buyerFilter}
              onChange={(e) => setBuyerFilter(e.target.value)}
              aria-label="Filtrar por comprador"
            >
              <option value="">Todos os compradores</option>
              {buyerOptions.map((b) => (
                <option key={b} value={b}>
                  {b}
                </option>
              ))}
            </select>
          </div>
        )}

        {isShoppingCenter && (
          <div className="col-12 col-md-3">
            <label className="form-label" style={{ fontSize: "14px" }}>
              Empresa
            </label>
            <select
              className="form-control"
              value={companyFilter}
              onChange={(e) => setCompanyFilter(e.target.value)}
              aria-label="Filtrar por empresa"
            >
              <option value="">Todas as empresas</option>
              {companyOptions.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="col-12 col-md-3 d-flex align-items-end justify-content-end">
          <button
            type="button"
            className="theme-btn-1 btn btn-effect-1"
            onClick={downloadOrdersReportPdfHandler}
            disabled={isDownloadingReportPdf || !token}
            title={isDownloadingReportPdf ? "Gerando PDF..." : "Baixar PDF do relatório"}
            style={{ padding: "10px 16px", width: "100%" }}
          >
            {isDownloadingReportPdf ? "Gerando PDF..." : "Baixar PDF"}
          </button>
        </div>
      </div>

      <div className="ltn__my-properties-table table-responsive col-12 mt-3">
        <table className="table">
          <thead
            style={{
              borderRight: "hidden",
              borderLeft: "hidden",
              borderTop: "hidden",
            }}
          >
            <tr>
              <th scope="col">Últimos pedidos</th>
              <th
                scope="col"
                className={styles.columnCenter}
                style={{ textAlign: "center" }}
              >
                Data
              </th>
              <th
                scope="col"
                className={styles.columnCenter}
                style={{ textAlign: "center" }}
              >
                Valor
              </th>
              <th
                scope="col"
                className={styles.columnCenter}
                style={{ textAlign: "center" }}
              >
                Visualizar
              </th>
            </tr>
          </thead>
          <tbody
            style={{
              borderRight: "hidden",
              borderLeft: "hidden",
              fontSize: "14px",
            }}
          >
            {filteredOrders
              .map((order, index) => (
                <tr key={index} className="mb-5">
                  <td>
                    <h6 className="mb-10 go-top">
                      Pedido N.º {order?.id_order}
                    </h6>
                    <p style={{ fontWeight: "bold" }} className="mb-2">{order.company_name}</p>
                    <p className="mb-2">{`Comprador: ${order?.buyer_name}`}</p>
                    <strong>
                      <i
                        className="fa-solid fa-circle"
                        style={{ color: order?.status_order_color, paddingRight: "8px" }}
                      ></i>
                      Pedido {order?.status_order_name}
                    </strong>
                  </td>
                  <td style={{ textAlign: "center" }}>{order?.order_date}</td>
                  <td style={{ textAlign: "center" }}>
                    {order?.total_price ?? "—"}
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <Link to="/detalhes-pedido" title="Ver detalhe da compra">
                      <button
                        style={{ padding: "16px", width: "max-content" }}
                        className="theme-btn-1 btn btn-block"
                        type="button"
                        title="Ver detalhe da compra"
                        onClick={() => loadOrderDetailHandler(order.id_order)}
                      >
                        <i className="icon-search for-search-show"></i>
                        Ver detalhes
                      </button>
                    </Link>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </>
  );
};